import { useState, useEffect } from 'react';
import { 
  Leaf, 
  Globe, 
  Server, 
  FileText, 
  ExternalLink, 
  AlertCircle,
  CheckCircle2,
  Info,
  Building2,
  FileCheck,
  Link as LinkIcon,
  HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVIDENCE_CATEGORIES = {
  certification: { label: 'Certification', icon: FileCheck },
  report: { label: 'Report', icon: FileText },
  audit: { label: 'Audit', icon: Building2 },
  statement: { label: 'Statement', icon: FileText }
};

const DISCLOSURE_STATUS = {
  disclosed: {
    label: 'Sustainability information disclosed by vendor',
    variant: 'default',
    icon: CheckCircle2
  },
  limited: {
    label: 'Limited sustainability information disclosed',
    variant: 'secondary',
    icon: Info
  },
  none: {
    label: 'No sustainability information disclosed',
    variant: 'outline',
    icon: AlertCircle
  }
};

export const SustainabilitySection = ({ platformId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSustainability = async () => {
      try {
        const response = await fetch(`${API_URL}/api/platforms/${platformId}/sustainability`);
        if (!response.ok) throw new Error('Failed to fetch');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSustainability();
  }, [platformId]);

  // Don't render if not enabled or no data
  if (loading) return null;
  if (error || !data?.enabled) return null;

  const { fields, evidence, disclosure_status, disclaimer, last_updated } = data;
  const statusConfig = DISCLOSURE_STATUS[disclosure_status] || DISCLOSURE_STATUS.none;
  const StatusIcon = statusConfig.icon;

  const hasFields = fields && (
    fields.infrastructure_providers ||
    (fields.regions_of_operation && fields.regions_of_operation.length > 0) ||
    fields.sustainability_commitments ||
    fields.carbon_reporting_available !== null ||
    fields.uses_offsets !== null
  );

  const hasEvidence = evidence && evidence.length > 0;

  if (!hasFields && !hasEvidence) return null;

  return (
    <TooltipProvider>
      <Card className="mt-6" data-testid="sustainability-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-green-600" />
              <CardTitle className="text-lg">Sustainability & Environmental Impact</CardTitle>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-muted-foreground hover:text-foreground transition-colors">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs p-4">
                  <p className="font-medium mb-2">About this section</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    This information is provided directly by the platform vendor. 
                    GoCompareAI displays it as-is without independent verification.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• We do not calculate carbon footprints</li>
                    <li>• We do not rank platforms on sustainability</li>
                    <li>• Verify claims directly with vendors</li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </div>
            <Badge variant={statusConfig.variant} className="gap-1.5">
              <StatusIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{statusConfig.label}</span>
            </Badge>
          </div>
          <CardDescription className="text-xs text-muted-foreground mt-2">
            Vendor-provided information
          </CardDescription>
        </CardHeader>

      <CardContent className="space-y-6">
        {/* Disclaimer */}
        <Alert className="bg-muted/50 border-muted">
          <Info className="w-4 h-4" />
          <AlertDescription className="text-xs">
            {disclaimer}
          </AlertDescription>
        </Alert>

        {/* Part A: Vendor-Provided Information */}
        {hasFields && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              Vendor-Provided Sustainability Information
            </h4>
            
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Infrastructure Providers */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Server className="w-3.5 h-3.5" />
                  Infrastructure Provider(s)
                </label>
                <p className="text-sm text-foreground">
                  {fields.infrastructure_providers || <span className="italic text-muted-foreground">Not disclosed</span>}
                </p>
              </div>

              {/* Regions of Operation */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" />
                  Regions of Operation
                </label>
                {fields.regions_of_operation && fields.regions_of_operation.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {fields.regions_of_operation.map((region, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {region}
                      </Badge>
                    ))}
                    {fields.regions_other && (
                      <Badge variant="outline" className="text-xs">
                        {fields.regions_other}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <p className="text-sm italic text-muted-foreground">Not disclosed</p>
                )}
              </div>

              {/* Carbon Reporting */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Carbon Reporting Available to Customers
                </label>
                <p className="text-sm text-foreground">
                  {fields.carbon_reporting_available === true ? 'Yes' : 
                   fields.carbon_reporting_available === false ? 'No' : 
                   <span className="italic text-muted-foreground">Not disclosed</span>}
                </p>
              </div>

              {/* Offsets/Mitigation */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Uses Offsets or Mitigation Measures
                </label>
                <p className="text-sm text-foreground">
                  {fields.uses_offsets === true ? 'Yes' : 
                   fields.uses_offsets === false ? 'No' : 
                   <span className="italic text-muted-foreground">Not disclosed</span>}
                </p>
              </div>
            </div>

            {/* Sustainability Commitments */}
            {fields.sustainability_commitments && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Sustainability or Environmental Commitments
                </label>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {fields.sustainability_commitments}
                </p>
              </div>
            )}

            {/* Offset Description */}
            {fields.uses_offsets && fields.offset_description && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Offset / Mitigation Details
                </label>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {fields.offset_description}
                </p>
              </div>
            )}
          </div>
        )}

        {hasFields && hasEvidence && <Separator />}

        {/* Part B: Evidence & Certifications */}
        {hasEvidence && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-muted-foreground" />
              Evidence & Certifications
            </h4>
            
            <div className="grid gap-3 sm:grid-cols-2">
              {evidence.map((item) => {
                const categoryConfig = EVIDENCE_CATEGORIES[item.category] || EVIDENCE_CATEGORIES.statement;
                const CategoryIcon = categoryConfig.icon;
                
                return (
                  <div 
                    key={item.id}
                    className="border rounded-lg p-3 space-y-2 bg-card hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <CategoryIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium text-sm">{item.certificate_name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {categoryConfig.label}
                      </Badge>
                    </div>
                    
                    <div className="text-xs text-muted-foreground space-y-1">
                      {item.issuing_organisation && (
                        <p>Issuer: {item.issuing_organisation}</p>
                      )}
                      {item.date_issued && (
                        <p>Issued: {item.date_issued}</p>
                      )}
                      <p>Scope: {item.scope === 'platform-specific' ? 'Platform-specific' : 'Organisation-wide'}</p>
                      <p className="flex items-center gap-1">
                        <span className="text-xs bg-muted px-1.5 py-0.5 rounded">Vendor-provided</span>
                      </p>
                    </div>
                    
                    {item.file_type === 'url' && item.file_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs h-8"
                        asChild
                      >
                        <a href={item.file_url} target="_blank" rel="noopener noreferrer">
                          <LinkIcon className="w-3.5 h-3.5 mr-1.5" />
                          View Document
                          <ExternalLink className="w-3 h-3 ml-1.5" />
                        </a>
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Last Updated */}
        {last_updated && (
          <p className="text-xs text-muted-foreground text-right">
            Last updated: {new Date(last_updated).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })}
          </p>
        )}
      </CardContent>
    </Card>
    </TooltipProvider>
  );
};

export default SustainabilitySection;
